import Media from "components/blocks/media"
import Paragraph from "components/type/Paragraph"
import PropTypes from "prop-types"
import React, { forwardRef, useImperativeHandle, useRef } from "react"
import "./card.scss"

const Card = forwardRef(
  (
    {
      ariaHidden,
      color = "dark-yellow",
      image,
      onNext,
      onPrevious,
      plain = false,
      quote,
      quoteAttribution,
      showNavigation = false,
      statistics,
      z,
    },
    ref
  ) => {
    const containerRef = useRef()

    useImperativeHandle(ref, () => ({
      get cardContainer() {
        return containerRef?.current
      },
    }))

    return (
      <div
        aria-hidden={ariaHidden}
        className={`testimonial-v2-card testimonial-v2-card__${color}${
          plain ? " testimonial-v2-card--plain" : ""
        }`}
        ref={containerRef}
        style={{ zIndex: z }}
      >
        <div className="testimonial-v2-card-wrapper">
          <div className="testimonial-v2-card__details">
            <div className="testimonial-v2-card__details-wrapper">
              {quote?.childMarkdownRemark?.html ? (
                <div
                  className="testimonial-v2-card__details-copy"
                  dangerouslySetInnerHTML={{
                    __html: quote.childMarkdownRemark.html,
                  }}
                />
              ) : (
                <Paragraph className="testimonial-v2-card__details-copy">
                  {quote.quote}
                </Paragraph>
              )}
              <p className="testimonial-v2-card__details-attribution">
                {!plain && <>&#8211;</>}
                {quoteAttribution}
              </p>
            </div>
            <div className="testimonial-v2-card__stats-wrapper">
              <ul className="testimonial-v2-card__stats-container">
                {(statistics || []).map((stat, i) => (
                  <li className="testimonial-v2-card__stats" key={i}>
                    <div className="testimonial-v2-card__stats-percent">
                      {stat.percent}%
                    </div>
                    <p className="testimonial-v2-card__stats-label">
                      {stat.copy}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
            {showNavigation && (
              <div className="testimonial-v2-card__navigation">
                <div
                  aria-label="go to previous card"
                  className="testimonial-v2-card__nav testimonial-v2-card__nav--prev"
                  onClick={onPrevious}
                  onKeyDown={onPrevious}
                  role="button"
                  tabIndex="0"
                >
                  <svg
                    fill="none"
                    height="45"
                    viewBox="0 0 44 45"
                    width="44"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <path
                        d="M0 22.21c0-12.151 9.85-22 22-22s22 9.849 22 22-9.85 22-22 22-22-9.85-22-22"
                        fill="#122f4e"
                      />
                      <path
                        d="m22.001 27.343-5.133-5.134m0 0L22 17.076m-5.133 5.133h10.266"
                        stroke="#f9f8f2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.463"
                      />
                    </g>
                  </svg>
                </div>
                <div
                  aria-label="go to next card"
                  className="testimonial-v2-card__nav testimonial-v2-card__nav--next"
                  onClick={onNext}
                  onKeyDown={onNext}
                  role="button"
                  tabIndex="0"
                >
                  <svg
                    fill="none"
                    height="45"
                    viewBox="0 0 45 45"
                    width="45"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M44.2 22.2092C44.2 34.3595 34.3503 44.2092 22.2 44.2092C10.0497 44.2092 0.200009 34.3595 0.20001 22.2092C0.200011 10.059 10.0498 0.209229 22.2 0.20923C34.3503 0.209231 44.2 10.059 44.2 22.2092Z"
                      fill="#122F4E"
                    />
                    <path
                      d="M22.1989 17.0759L27.3322 22.2092M27.3322 22.2092L22.1989 27.3425M27.3322 22.2092L17.0655 22.2092"
                      stroke="#F9F8F2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.463"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
)

Card.props = {
  ariaHidden: PropTypes.bool,
  color: PropTypes.oneOf([
    "dark-yellow",
    "pastel-blue",
    "blue",
    "pastel-pink",
    "pastel-green",
  ]),
  // Research study cards have no image.
  image: PropTypes.shape(Media.props),
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  // No quote marks and no dash before the attribution, for cards that are
  // not quotes (the "Research Studies" display style).
  plain: PropTypes.bool,
  quote: PropTypes.shape({
    quote: PropTypes.string.isRequired,
  }).isRequired,
  quoteAttribution: PropTypes.string,
  showNavigation: PropTypes.bool,
  statistics: PropTypes.arrayOf(
    PropTypes.shape({
      copy: PropTypes.string,
      percent: PropTypes.number,
    })
  ),
  z: PropTypes.number,
}

Card.displayName = "TestimonialCard"

Card.propTypes = Card.props

export default Card
